import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'
import { motion, AnimatePresence } from 'framer-motion'

// --- Internal Step Components (Ported from Admin) ---
import Step1BasicInfo from '../admin/project-steps/Step1BasicInfo'
import Step2Location from '../admin/project-steps/Step2Location'
import Step3Developer from '../admin/project-steps/Step3Developer'
import Step4FloorPlan from '../admin/project-steps/Step4FloorPlan'
import Step5Inventory from '../admin/project-steps/Step5Inventory'
import Step6Pricing from '../admin/project-steps/Step5Pricing'
import StepSEOTimeline from '../admin/project-steps/StepSEOTimeline'
import Step8Grid from '../admin/project-steps/Step8Grid'
import Step7Content from '../admin/project-steps/Step6Content'
import Step8Media from '../admin/project-steps/Step7Media'
import Step10Offers from '../admin/project-steps/Step9Offers'
import StepContactInfo from '../admin/project-steps/StepContactInfo'

// --- Types & Constants ---
import { 
    ProjectForm, 
    FloorPlan, 
    AdditionalDetail, 
    Specification, 
    LegalDocument, 
    FAQ, 
    NearbyPlace, 
    InventoryItem,
    GalleryCategory,
    GalleryState,
    GalleryFileState
} from '../admin/project-steps/types'
import { 
    SUBCATEGORIES, 
    STEPS, 
    NEARBY_CATEGORY_ICONS, 
    DEFAULT_CONSTRUCTION_STATUS_OPTIONS 
} from '../admin/project-steps/constants'

const DEV_STEPS = [
    'Basic Details',
    'Location Details',
    'Developer Info',
    'Floor Variants (Config)',
    'SEO Details',
    'Gallery & Documents',
    'Content & Highlights',
    'Offers & Pricing',
    'Contact Info'
];

export default function DevAddProject() {
    const { token } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams()
    const [currentStep, setCurrentStep] = useState(1)
    const [activeStep4Tab, setActiveStep4Tab] = useState<'2d' | '3d' | null>('2d')
    const [submitting, setSubmitting] = useState(false)
    const [submittingPkgId, setSubmittingPkgId] = useState<string | null>(null)
    const [selectedPkg, setSelectedPkg] = useState<any>(null)
    const [transactionId, setTransactionId] = useState('')
    const [submittingPurchase, setSubmittingPurchase] = useState(false)
    const [adminUpiId, setAdminUpiId] = useState('admin@upi')
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [errors, setErrors] = useState<Record<string, boolean>>({})
    const [activeProjectId, setActiveProjectId] = useState<string | null>(id || null)
    const [userProfile, setUserProfile] = useState<any>(null);
    const [availablePackages, setAvailablePackages] = useState<any[]>([]);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [scoreSettings, setScoreSettings] = useState<any>(null);

    // Form Master State
    const [form, setForm] = useState<ProjectForm>({
        name: '', listingType: 'For Sale', propertyType: ['Residential'], subCategory: ['Flat/Apartment'], projectStatus: 'Under Construction', progressPercentage: 0, tagline: '', slug: '', isOurPick: false,
        location: '', address: '', city: '', state: 'Gujarat', country: 'India', pinCode: '', googleMapLocation: '', google3dViewLink: '', latitude: '', longitude: '',
        developerName: '', coDeveloper: '', developerLogo: '', developerWebsite: '', developerDescription: '', developerTagline: '', totalProjects: '0', projectsCompleted: '0', projectsOngoing: '0',
        projectsUpcoming: '0',
        projectsSince: '',
        aboutDeveloper: '',
        developerId: '',
        overviewConfiguration: '', overviewSizeRange: '', overviewPriceRange: '', overviewAvgPricePerSqFt: '', overviewProjectArea: '', overviewTotalPlotArea: '', overviewOpenSpacePercentage: '', overviewTotalTowers: '', overviewTotalFloors: '', overviewTotalUnits: '', overviewFloors: '',
        overviewAmenitiesIcon: '',
        towers: [],
        launchDate: '', possessionDate: '', timelineStatus: '', constructionStatusScore: 0,
        reraApproved: 'Yes', reraNumber: '', reraAuthority: 'Gujarat Real Estate Regulatory Authority', reraLink: '',
        shortDescription: '', fullDescription: '', 
        highlights: [
            { icon: 'fa-location-dot', text: 'Prime Location' },
            { icon: 'fa-city', text: 'Premium Residences' },
            { icon: 'fa-leaf', text: '65% Open Space' }
        ], 
        investmentPotential: '', videoUrl: '',
        brochure: null,
        brochureUrl: '',
        images: [],
        virtualTours: [{ title: '', link: '' }],
        youtubeVideos: [{ title: '', link: '' }],
        builderOffers: [{ offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }],
        faq: [{ question: '', answer: '' }],
        metaTitle: '', metaDescription: '', metaKeywords: '', canonicalUrl: '', focusKeyword: '',
        contactNumber: '', contactPhone: '', whatsappNumber: '', contactEmail: '',
        disclaimer: '',
        priceBreakup: [{ label: 'Basic Cost', value: '' }, { label: 'Extra Charges', value: '' }, { label: 'GST / Taxes', value: '' }],
        paymentPlans: [{ title: 'Possession Linked', description: 'Pay 25% now, rest on possession' }],
        bankApprovals: [],
        tpImage: '',
        tpImageUrl: '',
        builderTrustScore: 0,
        localityTrustScore: 0,
        loadingScore: 0,
        legalTrustScore: 0,
        amenitiesScore: 0,
        specificationsScore: 0,
        status: 'Pending',
        nearbyPlaces: [],
        dynamicSlots: [],
        sectionInfo: {
            overview: '',
            amenities: '',
            specifications: '',
            location: '',
            floorPlans: '',
            gallery: '',
            developer: '',
            nearby: '',
            about: '',
            highlights: '',
            faq: '',
            reviews: ''
        }
    })

    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false)

    // Complex Dynamic Fields
    const [additionalDetails, setAdditionalDetails] = useState<AdditionalDetail[]>([{ label: '', value: '', icon: '' }])
    const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([{
        unitType: '',
        subUnits: [{ subUnitType: '', subUnitVariant: '', price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '' }]
    }])
    const [inventory, setInventory] = useState<InventoryItem[]>([{ tower: '', floor: '', unitNo: '', unitType: '', configuration: '', size: '', price: '', status: 'Available' }])
    const [specifications, setSpecifications] = useState<Specification[]>([])
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([])
    const [globalAmenities, setGlobalAmenities] = useState<{ _id: string, name: string, icon: string, score: number }[]>([])
    const [globalSpecs, setGlobalSpecs] = useState<{ _id: string, name: string, icon: string, details: string }[]>([])
    const [globalDetails, setGlobalDetails] = useState<{ _id: string, label: string, icon: string }[]>([])
    const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([{ title: '', image: '', imagePreview: '' }])
    const [globalLandmarkCategories, setGlobalLandmarkCategories] = useState<{ _id: string, name: string, icon: string }[]>([])
    const [faqs, setFaqs] = useState<FAQ[]>([{ question: '', answer: '' }])
    const [developers, setDevelopers] = useState<{
        _id: string, name: string, logo?: string, website?: string, totalProjects?: string, projectsCompleted?: string, projectsOngoing?: string, projectsUpcoming?: string;
        projectsSince?: string;
        deliveryRecord?: string;
        email?: string;
    }[]>([]);
    const [cities, setCities] = useState<{ _id: string, name: string }[]>([])
    const [allLocalities, setAllLocalities] = useState<{ _id: string, name: string, city: any }[]>([])
    const [filteredLocalities, setFilteredLocalities] = useState<{ _id: string, name: string }[]>([])
    const [imagePreviews, setImagePreviews] = useState<string[]>([])
    const [galleryPreviews, setGalleryPreviews] = useState<GalleryState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [existingGallery, setExistingGallery] = useState<GalleryState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [galleryIcons, setGalleryIcons] = useState<Record<string, string>>({})

    // Media State
    const [featuredImage, setFeaturedImage] = useState<File | null>(null)
    const [featuredPreview, setFeaturedPreview] = useState('')
    const [tpImage, setTpImage] = useState<File | null>(null)
    const [tpPreview, setTpPreview] = useState('')
    const [gallery, setGallery] = useState<GalleryFileState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [brochureFile, setBrochureFile] = useState<File | null>(null)
    const [constructionStatusImage, setConstructionStatusImage] = useState<File | null>(null)
    const [constructionStatusPreview, setConstructionStatusPreview] = useState('')
    const [seoImageFile, setSeoImageFile] = useState<File | null>(null)
    const [seoImagePreview, setSeoImagePreview] = useState('')

    const handleGalleryUpload = (category: string, files: File[]) => {
        setGallery(prev => ({ ...prev, [category]: [...(prev[category] || []), ...files] }));
        const newPreviews = files.map(f => URL.createObjectURL(f));
        setGalleryPreviews(prev => ({ ...prev, [category]: [...(prev[category] || []), ...newPreviews] }));
    };

    const updateGalleryIcon = async (album: string, icon: File | string) => {
        if (typeof icon === 'string') {
            setGalleryIcons(prev => ({ ...prev, [album]: icon }));
        } else {
            try {
                const urls = await handleUpload([icon]);
                if (urls[0]) {
                    setGalleryIcons(prev => ({ ...prev, [album]: urls[0] }));
                }
            } catch (err) {
                toast.error("Failed to upload icon");
            }
        }
    };

    const removeGalleryCategory = (album: string) => {
        const newExGallery = { ...existingGallery };
        delete newExGallery[album];
        setExistingGallery(newExGallery);

        const newGallery = { ...gallery };
        delete newGallery[album];
        setGallery(newGallery);

        const newPreviews = { ...galleryPreviews };
        delete newPreviews[album];
        setGalleryPreviews(newPreviews);

        const newIcons = { ...galleryIcons };
        delete newIcons[album];
        setGalleryIcons(newIcons);
    };

    const removeGalleryImage = (category: string, idx: number) => {
        const existingCount = (existingGallery[category] || []).length;
        if (idx < existingCount) {
            setExistingGallery(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
            setGalleryPreviews(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
        } else {
            const adjustedIdx = idx - existingCount;
            setGallery(prev => ({ ...prev, [category]: (prev[category] || []).filter((_: File, i: number) => i !== adjustedIdx) }));
            setGalleryPreviews(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
        }
    };

    const syncStateWithProject = (data: any) => {
        if (!data) return;
        setForm({
            name: data.name || '',
            listingType: data.listingType || 'For Sale',
            propertyType: Array.isArray(data.propertyType) ? data.propertyType : (data.propertyType ? [data.propertyType] : []),
            subCategory: Array.isArray(data.subCategory) ? data.subCategory : (data.subCategory ? [data.subCategory] : []),
            projectStatus: data.projectStatus || 'Under Construction',
            progressPercentage: data.progressPercentage || 0,
            tagline: data.tagline || '',
            slug: data.slug || '',
            isOurPick: data.isOurPick || false,
            location: data.location || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || 'Gujarat',
            country: data.country || 'India',
            pinCode: data.pinCode || '',
            googleMapLocation: data.mapDetails?.googleMapLocation || '',
            google3dViewLink: data.mapDetails?.google3dViewLink || '',
            latitude: data.mapDetails?.latitude || '',
            longitude: data.mapDetails?.longitude || '',
            developerName: data.developerDetails?.name || '',
            coDeveloper: data.developerDetails?.coDeveloper || '',
            developerLogo: data.developerDetails?.logo || '',
            developerWebsite: data.developerDetails?.website || '',
            developerDescription: data.developerDetails?.description || '',
            developerTagline: data.developerDetails?.tagline || '',
            totalProjects: data.developerDetails?.totalProjects || '0',
            projectsCompleted: data.developerDetails?.projectsCompleted || '0',
            projectsOngoing: data.developerDetails?.projectsOngoing || '0',
            projectsUpcoming: data.developerDetails?.projectsUpcoming || '0',
            projectsSince: data.developerDetails?.projectsSince || '',
            aboutDeveloper: data.developerDetails?.description || '',
            developerId: data.developer || '',
            overviewConfiguration: data.overview?.configuration || '',
            overviewSizeRange: data.overview?.sizeRange || '',
            overviewPriceRange: data.overview?.priceRange || '',
            overviewAvgPricePerSqFt: data.overview?.avgPricePerSqFt || '',
            overviewProjectArea: data.overview?.projectArea || '',
            overviewTotalPlotArea: data.overview?.totalPlotArea || '',
            overviewOpenSpacePercentage: data.overview?.openSpacePercentage || '',
            overviewTotalTowers: data.overview?.totalTowers || '',
            overviewTotalFloors: data.overview?.totalFloors || '',
            overviewTotalUnits: data.overview?.totalUnits || '',
            overviewFloors: data.overview?.floors || '',
            overviewAmenitiesIcon: '',
            towers: data.towers || [],
            launchDate: data.launchDate ? new Date(data.launchDate).toISOString().split('T')[0] : '',
            possessionDate: data.possessionDate ? new Date(data.possessionDate).toISOString().split('T')[0] : '',
            timelineStatus: data.constructionStatus || '',
            constructionStatusScore: data.constructionStatusScore || 0,
            reraApproved: data.reraApproved || 'Yes',
            reraNumber: data.reraNumber || '',
            reraAuthority: data.reraAuthority || 'Gujarat Real Estate Regulatory Authority',
            reraLink: data.reraLink || '',
            shortDescription: data.shortDescription || '',
            fullDescription: data.description || '',
            highlights: data.highlights || [],
            investmentPotential: data.investmentPotential || '',
            videoUrl: data.videoUrl || '',
            brochure: null,
            brochureUrl: data.brochureUrl || '',
            images: data.gallery?.exterior || [],
            virtualTours: data.virtualTours?.length > 0 ? data.virtualTours : [{ title: '', link: '' }],
            youtubeVideos: data.youtubeVideos?.length > 0 ? data.youtubeVideos : [{ title: '', link: '' }],
            builderOffers: data.builderOffers?.length > 0 ? data.builderOffers : [{ offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }],
            faq: data.faq?.length > 0 ? data.faq : [{ question: '', answer: '' }],
            metaTitle: data.metaTitle || '',
            metaDescription: data.metaDescription || '',
            metaKeywords: data.metaKeywords || '',
            canonicalUrl: data.canonicalUrl || '',
            focusKeyword: data.focusKeyword || '',
            contactNumber: data.contactNumber || '',
            contactPhone: data.contactPhone || '',
            whatsappNumber: data.whatsappNumber || '',
            contactEmail: data.contactEmail || '',
            disclaimer: data.disclaimer || '',
            priceBreakup: data.priceBreakup?.length > 0 ? data.priceBreakup : [{ label: 'Basic Cost', value: '' }, { label: 'Extra Charges', value: '' }, { label: 'GST / Taxes', value: '' }],
            paymentPlans: data.paymentPlans?.length > 0 ? data.paymentPlans : [{ title: 'Possession Linked', description: 'Pay 25% now, rest on possession' }],
            bankApprovals: data.bankApprovals || [],
            tpImage: '',
            tpImageUrl: data.tpImage || '',
            builderTrustScore: data.builderTrustScore || 0,
            localityTrustScore: data.localityTrustScore || 0,
            loadingScore: data.loadingScore || 0,
            legalTrustScore: data.legalTrustScore || 0,
            amenitiesScore: data.amenitiesScore || 0,
            specificationsScore: data.specificationsScore || 0,
            status: data.status || 'Pending',
            nearbyPlaces: data.nearbyPlaces || [],
            dynamicSlots: data.dynamicSlots || [],
            sectionInfo: data.sectionInfo || {
                overview: '', amenities: '', specifications: '', location: '', floorPlans: '', gallery: '', developer: '', nearby: '', about: '', highlights: '', faq: '', reviews: ''
            }
        });

        if (data.floorPlans && data.floorPlans.length > 0) {
            setFloorPlans(data.floorPlans.map((fp: any) => ({
                unitType: fp.unitType,
                subUnits: fp.subUnits.map((su: any) => ({
                    ...su,
                    imagePreview: su.image ? getImageUrl(su.image) : '',
                    image2DPreview: su.image2D ? getImageUrl(su.image2D) : '',
                    image3DPreview: su.image3D ? getImageUrl(su.image3D) : '',
                    imageFile: undefined,
                    image2DFile: undefined,
                    image3DFile: undefined
                }))
            })));
        }

        if (data.gallery) {
            const eg: GalleryState = {
                exterior: data.gallery.exterior || [],
                interior: data.gallery.interior || [],
                amenities: data.gallery.amenities || [],
                construction: data.gallery.construction || []
            };
            Object.keys(data.gallery).forEach(key => {
                if (!['exterior', 'interior', 'amenities', 'construction'].includes(key)) {
                    eg[key] = data.gallery[key] || [];
                }
            });
            setExistingGallery(eg);

            const previews: GalleryState = { exterior: [], interior: [], amenities: [], construction: [] };
            Object.keys(eg).forEach(key => {
                previews[key] = eg[key].map((u: string) => getImageUrl(u));
            });
            setGalleryPreviews(previews);

            if (data.galleryIcons) {
                const icons: Record<string, string> = {};
                Object.keys(data.galleryIcons).forEach(k => {
                    icons[k] = data.galleryIcons[k].startsWith('http') ? getImageUrl(data.galleryIcons[k]) : data.galleryIcons[k];
                });
                setGalleryIcons(icons);
            }
        }

        if (data.inventory) setInventory(data.inventory);
        if (data.amenities) setSelectedAmenities(data.amenities);
        if (data.specifications) setSpecifications(data.specifications);
        if (data.additionalDetails) setAdditionalDetails(data.additionalDetails);
        if (data.legalDocuments) {
            setLegalDocuments(data.legalDocuments.map((ld: any) => ({
                ...ld,
                imagePreview: ld.image ? getImageUrl(ld.image) : '',
                file: undefined
            })));
        }
        if (data.featuredImage) setFeaturedPreview(getImageUrl(data.featuredImage));
        if (data.tpImage) setTpPreview(getImageUrl(data.tpImage));

        // Reset temporary File upload variables
        setFeaturedImage(null);
        setTpImage(null);
        setBrochureFile(null);
        setGallery({ exterior: [], interior: [], amenities: [], construction: [] });
    };

    const loadProjectData = async (projectId: string) => {
        try {
            const data = await api.getProjectById(projectId);
            if (!data || data.message) {
                toast.error("Project not found");
                return;
            }
            syncStateWithProject(data);
            const settings = await api.getScoreSettings();
            if (settings && !settings.message) {
                setScoreSettings(settings);
            }
        } catch (err) {
            console.error("Failed to load project:", err);
            toast.error("Error loading project details");
        }
    };

    useEffect(() => {
        if (token) {
            loadProfile();
            if (id) {
                loadProjectData(id);
            }
        } else {
            setLoadingProfile(false);
        }
    }, [token, id]);

    const loadProfile = async () => {
        try {
            setLoadingProfile(true);
            const profile = await api.getUserProfile(token!);
            setUserProfile(profile);

            // Prefill with logged-in developer profile details by default
            if (profile.role === 'developer') {
                setForm(p => ({
                    ...p,
                    developerId: profile._id,
                    developerName: profile.name,
                    developerLogo: profile.avatar || '',
                    developerWebsite: profile.website || '',
                    totalProjects: profile.totalProjects || '0',
                    projectsCompleted: profile.projectsCompleted || '0',
                    projectsOngoing: profile.projectsOngoing || '0',
                    projectsUpcoming: profile.projectsUpcoming || '0',
                    projectsSince: profile.projectsSince || ''
                }));
            }
            
            // Fetch Developers to find match for extra verification
            const devsRes = await fetch(`${API_BASE}/developers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const devsData = await devsRes.json();
            if (Array.isArray(devsData)) {
                setDevelopers(devsData);
                // Match by email or name
                const myDev = devsData.find(d => d.email === profile.email || d.name === profile.name);
                if (myDev) {
                    setForm(p => ({
                        ...p,
                        developerId: myDev._id,
                        developerName: myDev.name,
                        developerLogo: myDev.avatar || myDev.logo || '',
                        developerWebsite: myDev.website || '',
                        totalProjects: myDev.totalProjects || '0',
                        projectsCompleted: myDev.projectsCompleted || '0',
                        projectsOngoing: myDev.projectsOngoing || '0',
                        projectsUpcoming: myDev.projectsUpcoming || '0',
                        projectsSince: myDev.projectsSince || ''
                    }));
                }
            }

            // Check if user has active package
            // packageExpiry null/undefined = Unlimited (no expiry)
            const hasActivePackage = !!(profile.assignedPackage) && 
                (!profile.packageExpiry || new Date(profile.packageExpiry) > new Date());

            if (!hasActivePackage) {
                const packages = await api.getPackages('developer');
                setAvailablePackages(packages);
                try {
                    const settings = await api.getSystemSettings();
                    if (settings?.adminUpiId) setAdminUpiId(settings.adminUpiId);
                } catch (e) {}
            }
        } catch (error) {
            console.error("Error loading profile:", error);
        } finally {
            setLoadingProfile(false);
        }
    };

    const openCheckout = (pkg: any) => {
        setSelectedPkg(pkg);
        setTransactionId('');
    };

    const closeCheckout = () => {
        setSelectedPkg(null);
        setTransactionId('');
    };

    const handleFreePurchase = async (pkg: any) => {
        if (!token) return;
        setSubmittingPkgId(pkg._id);
        try {
            const res = await api.assignPackage(token, pkg._id);
            const isSuccess = res && (
                res.success === true ||
                res._id ||
                res.assignedPackage ||
                (res.message && res.message.toLowerCase().includes('success')) ||
                (!res.error && !res.message?.toLowerCase().includes('error') && !res.message?.toLowerCase().includes('fail'))
            );
            if (isSuccess) {
                toast.success(`🎉 "${pkg.name}" package activated successfully! You can now add your project.`);
                // Reload profile to reflect the new package
                await loadProfile();
            } else {
                toast.error(res?.message || 'Failed to activate package. Please try again.');
            }
        } catch (err) {
            toast.error('Server error. Could not activate free package.');
        } finally {
            setSubmittingPkgId(null);
        }
    };

    const handlePurchase = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token || !selectedPkg) return;
        if (!transactionId.trim()) {
            toast.error('Please enter a valid transaction reference number');
            return;
        }

        setSubmittingPurchase(true);
        try {
            await api.purchasePlan(token, {
                packageId: selectedPkg._id,
                name: selectedPkg.name,
                price: selectedPkg.price,
                duration: selectedPkg.period || 'Unlimited',
                users: '1',
                leadAccess: 'Developer Package',
                dailyUnlock: 'Unlimited',
                transactionId
            });
            toast.success('Purchase submitted successfully! Admin will review your transaction.');
            closeCheckout();
        } catch (err) {
            toast.error('Failed to submit purchase request');
        } finally {
            setSubmittingPurchase(false);
        }
    };

    const extractAmount = (priceStr: string) => {
        if (!priceStr) return '';
        const matches = priceStr.toString().match(/\d+/g);
        return matches ? matches.join('') : '';
    };

    const handlePackageInquiry = async (packageId: string) => {
        if (submittingPkgId) return;
        try {
            setSubmittingPkgId(packageId);
            const res = await api.sendPackageInquiry(token!, packageId);
            if (res.success) {
                toast.success("Inquiry sent! Our team will contact you to activate your developer package.");
            } else {
                toast.error(res.message || "Failed to send inquiry");
            }
        } catch (error) {
            console.error("Package inquiry error:", error);
            toast.error("Server connection error.");
        } finally {
            setSubmittingPkgId(null);
        }
    };

    useEffect(() => {
        fetch(`${API_BASE}/amenities`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setGlobalAmenities(data) })
            .catch(console.error)

        fetch(`${API_BASE}/specifications`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setGlobalSpecs(data) })
            .catch(console.error)

        fetch(`${API_BASE}/project-details`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setGlobalDetails(data) })
            .catch(console.error)

        fetch(`${API_BASE}/cities`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setCities(data.filter((c: any) => c.isActive)) })
            .catch(console.error)

        fetch(`${API_BASE}/localities`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setAllLocalities(data.filter((l: any) => l.isActive)) })
            .catch(console.error)

        fetch(`${API_BASE}/landmark-categories`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setGlobalLandmarkCategories(data) })
            .catch(console.error)

    }, [])

    useEffect(() => {
        if (isSlugManuallyEdited || !form.name) return;
        const slugify = (text: string) => {
            return text
                .toString()
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
                .replace(/^-+|-+$/g, '');
        };

        const namePart = slugify(form.name);
        const locationPart = form.location ? slugify(form.location) : '';
        const cityPart = form.city ? slugify(form.city) : '';

        const parts = [namePart, locationPart, cityPart].filter(Boolean);
        const newSlug = parts.join('-');

        setForm(prev => {
            if (prev.slug === newSlug) return prev;
            return { ...prev, slug: newSlug };
        });
    }, [form.name, form.location, form.city, isSlugManuallyEdited]);
    

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        if (errors[name]) {
            setErrors(prev => {
                const newErrs = { ...prev };
                delete newErrs[name];
                return newErrs;
            });
        }

        if (name === 'propertyType') {
            const valStr = value as string;
            setForm(p => {
                const newTypes = p.propertyType.includes(valStr) 
                    ? p.propertyType.filter(t => t !== valStr) 
                    : [...p.propertyType, valStr];
                
                // Keep subcategories that are still valid for the selected property types
                const validSubs = new Set<string>();
                newTypes.forEach(t => {
                    (SUBCATEGORIES[t] || []).forEach(s => validSubs.add(s));
                });
                const newSubs = p.subCategory.filter(s => validSubs.has(s));
                
                return { ...p, propertyType: newTypes, subCategory: newSubs };
            });
        } else if (name === 'subCategory') {
            const valStr = value as string;
            setForm(p => {
                const newSubs = p.subCategory.includes(valStr)
                    ? p.subCategory.filter(s => s !== valStr)
                    : [...p.subCategory, valStr];
                return { ...p, subCategory: newSubs };
            });
        } else if (name === 'developerId') {
            const dev = developers.find(d => d._id === value);
            setForm(p => ({
                ...p,
                developerId: value,
                developerName: dev?.name || '',
                developerLogo: dev?.logo || p.developerLogo,
                developerWebsite: dev?.website || p.developerWebsite,
                totalProjects: dev?.totalProjects || '0',
                projectsCompleted: dev?.projectsCompleted || '0',
                projectsOngoing: dev?.projectsOngoing || '0',
                projectsUpcoming: dev?.projectsUpcoming || '0',
                projectsSince: dev?.projectsSince || ''
            }))
        } else if (name === 'city') {
            const cityName = value;
            setForm(p => ({ ...p, city: cityName, location: '' }));
            const filtered = allLocalities.filter(l => {
                const cityObj = l.city;
                const cityMatch = (cityObj && typeof cityObj === 'object') ? cityObj.name === cityName : cityObj === cityName;
                return cityMatch;
            });
            setFilteredLocalities(filtered);
        } else if (name === 'timelineStatus') {
            const options = scoreSettings?.constructionStatusOptions?.length > 0 
                ? scoreSettings.constructionStatusOptions 
                : DEFAULT_CONSTRUCTION_STATUS_OPTIONS;
            const option = options.find((o: any) => o.label === value);
            const getRequiredTitles = (status: string) => {
                if (status === 'RERA Approved') return ["Title Deed"];
                if (status === 'Under Construction' || status === 'Near Possession') return ["RERA Certificate", "Title Deed", "Approved Plan"];
                if (status === 'Ready to Move') return ["RERA Certificate", "Title Deed", "Approved Plan", "Occupancy Certificate (OC)"];
                return [];
            };
            const titles = getRequiredTitles(value);
            setLegalDocuments(prev => {
                const existing = [...prev];
                return titles.map(t => {
                    const found = existing.find(ex => ex.title === t);
                    return found ? found : { title: t, image: '', imagePreview: '', link: '' };
                });
            });
            setForm(p => ({ ...p, timelineStatus: value, constructionStatusScore: option ? option.score : 0 }));
        } else if (name === 'slug') {
            setIsSlugManuallyEdited(true);
            setForm(p => ({ ...p, slug: value }));
        } else if (name === 'name') {
            setForm(p => ({ ...p, name: value }));
        } else {
            setForm(p => ({ ...p, [name]: val }))
        }
    }

    const nextStep = () => setCurrentStep(s => Math.min(s + 1, 9))
    const prevStep = () => setCurrentStep(s => Math.max(s - 1, 1))

    const handleUpload = async (files: File[]) => {
        if (!token || files.length === 0) return []
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('images', file));
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (!res.ok) {
                toast.error(`Upload failed: ${data.message || 'Server error'}`);
                return [];
            }
            return data.urls || []
        } catch (err) {
            console.error('Upload failed', err)
            toast.error('Connection error during upload.');
            return []
        }
    }

    const handleSubmit = async (e?: React.FormEvent, statusOverride?: string, silent?: boolean) => {
        if (e) e.preventDefault()
        if (!token) {
            if (!silent) setMsg({ type: 'error', text: 'You are not logged in.' })
            return false
        }
        
        const isDraft = statusOverride === 'Draft'
        if (!silent) setSubmitting(true)

        if (!isDraft) {
            if (!form.name.trim()) { setMsg({ type: 'error', text: 'Project Name is required' }); setCurrentStep(1); if (!silent) setSubmitting(false); return false; }
            if (!form.location.trim()) { setMsg({ type: 'error', text: 'Area / Locality is required' }); setCurrentStep(2); if (!silent) setSubmitting(false); return false; }
        }

        try {
            let featuredUrl = ''
            if (featuredImage) { const urls = await handleUpload([featuredImage]); featuredUrl = urls[0] || ''; }

            const galleryUrls: Record<string, string[]> = {};
            const allCategories = Array.from(new Set([...Object.keys(existingGallery), ...Object.keys(gallery)]));
            for (const category of allCategories) {
                const existing = existingGallery[category] || [];
                const newlyUploaded = await handleUpload(gallery[category] || []);
                galleryUrls[category] = [...existing, ...newlyUploaded];
            }

            let tpUrl = ''
            if (tpImage) { const urls = await handleUpload([tpImage]); tpUrl = urls[0] || ''; }

            let brochureUrl = form.brochureUrl || ''
            if (brochureFile) {
                const fd = new FormData(); fd.append('document', brochureFile);
                const res = await fetch(`${API_BASE}/upload/document`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                const data = await res.json(); brochureUrl = data.url || '';
            }

            let constructionStatusImageUrl = ''
            if (constructionStatusImage) { const urls = await handleUpload([constructionStatusImage]); constructionStatusImageUrl = urls[0] || ''; }

            const payload = {
                ...form,
                status: statusOverride || 'Pending',
                developer: form.developerId || undefined,
                developerDetails: {
                    name: form.developerName,
                    coDeveloper: form.coDeveloper,
                    logo: form.developerLogo,
                    website: form.developerWebsite,
                    description: form.developerDescription,
                    tagline: form.developerTagline,
                    totalProjects: form.totalProjects,
                    projectsCompleted: form.projectsCompleted,
                    projectsOngoing: form.projectsOngoing,
                    projectsUpcoming: form.projectsUpcoming,
                    projectsSince: form.projectsSince
                },
                overview: {
                    configuration: form.overviewConfiguration,
                    sizeRange: form.overviewSizeRange,
                    priceRange: form.overviewPriceRange,
                    avgPricePerSqFt: form.overviewAvgPricePerSqFt,
                    projectArea: form.overviewProjectArea,
                    totalPlotArea: form.overviewTotalPlotArea,
                    openSpacePercentage: form.overviewOpenSpacePercentage,
                    totalTowers: form.overviewTotalTowers,
                    totalFloors: form.overviewTotalFloors,
                    totalUnits: form.overviewTotalUnits,
                    floors: form.overviewFloors
                },
                mapDetails: {
                    googleMapLocation: form.googleMapLocation,
                    google3dViewLink: form.google3dViewLink,
                    latitude: form.latitude,
                    longitude: form.longitude,
                },
                virtualTours: form.virtualTours.filter(vt => vt.link),
                youtubeVideos: form.youtubeVideos.filter(yv => yv.link),
                inventory: inventory.filter(inv => inv.tower || inv.unitNo),
                priceBreakup: form.priceBreakup,
                paymentPlans: form.paymentPlans,
                bankApprovals: form.bankApprovals,
                highlights: form.highlights,
                metaTitle: form.metaTitle,
                metaDescription: form.metaDescription,
                metaKeywords: form.metaKeywords,
                canonicalUrl: form.canonicalUrl,
                focusKeyword: form.focusKeyword,
                contactNumber: form.contactNumber,
                contactEmail: form.contactEmail,
                disclaimer: form.disclaimer,
                isOurPick: form.isOurPick,
                subCategory: form.subCategory,
                projectStatus: form.projectStatus,
                progressPercentage: form.progressPercentage,
                tagline: form.tagline,
                city: form.city,
                location: form.location,
                address: form.address,
                state: form.state,
                country: form.country,
                pinCode: form.pinCode,
                slug: form.slug.trim() || form.name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, ''),
                launchDate: form.launchDate || undefined,
                possessionDate: form.possessionDate || undefined,
                constructionStatus: form.timelineStatus,
                constructionStatusScore: form.constructionStatusScore,
                constructionStatusImage: constructionStatusImageUrl,
                featuredImage: featuredUrl,
                tpImage: tpUrl,
                gallery: galleryUrls,
                galleryIcons: galleryIcons,
                brochureUrl: brochureUrl,
                legalDocuments: await Promise.all(legalDocuments.filter(ld => ld.title || ld.imagePreview || ld.link).map(async ld => ({
                    title: ld.title,
                    image: ld.file ? (await handleUpload([ld.file]))[0] : ld.image,
                    link: ld.link
                }))),
                description: form.fullDescription,
                nearbyPlaces: form.nearbyPlaces,
                builderOffers: form.builderOffers.filter(offer => offer.offerType),
                investmentPotential: form.investmentPotential,
                additionalDetails: additionalDetails.filter(d => d.label),
                floorPlans: await Promise.all(floorPlans.filter(fp => fp.unitType.trim() !== '' || fp.subUnits.some(su => su.subUnitVariant.trim() !== '')).map(async fp => ({
                    unitType: fp.unitType,
                    subUnits: await Promise.all(fp.subUnits.map(async su => ({
                        subUnitType: su.subUnitType,
                        subUnitVariant: su.subUnitVariant,
                        price: su.price,
                        facing: su.facing,
                        reraArea: su.reraArea,
                        carpetArea: su.carpetArea,
                        builtUpArea: su.builtUpArea,
                        superBuiltUpArea: su.superBuiltUpArea,
                        details: su.details.filter(d => d.label),
                        image: su.imageFile ? (await handleUpload([su.imageFile]))[0] : su.image,
                        image2D: su.image2DFile ? (await handleUpload([su.image2DFile]))[0] : su.image2D,
                        image3D: su.image3DFile ? (await handleUpload([su.image3DFile]))[0] : su.image3D
                    })))
                }))),
                amenities: selectedAmenities,
                specifications: specifications.map(s => ({ category: s.category, details: s.details, icon: s.icon })),
                builderTrustScore: 0, 
                localityTrustScore: (() => {
                    let score = 0;
                    const rules = scoreSettings?.localityScoreRules || [];
                    const usedCategories = new Set();
                    
                    rules.forEach((rule: any) => {
                        if (!usedCategories.has(rule.category)) {
                            const hasMatch = form.nearbyPlaces.some(place => {
                                const distance = parseFloat(place.distance) || 0;
                                return place.category === rule.category && distance > 0 && distance <= rule.maxDistanceKm;
                            });
                            
                            if (hasMatch) {
                                score += rule.points;
                                usedCategories.add(rule.category);
                            }
                        }
                    });
                    return score;
                })(),
                loadingScore: 0,
                legalTrustScore: 0,
                amenitiesScore: 0,
                specificationsScore: 0
            }

            const url = activeProjectId ? `${API_BASE}/projects/${activeProjectId}` : `${API_BASE}/projects`
            const method = activeProjectId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            })

            const responseData = await res.json()
            if (res.ok) {
                if (!activeProjectId && responseData._id) setActiveProjectId(responseData._id)
                
                // 6. Synchronize Inventory
                const syncProjectId = activeProjectId || responseData._id;
                if (syncProjectId && inventory.length > 0) {
                    await api.bulkSyncInventory(token, syncProjectId, inventory);
                }

                syncStateWithProject(responseData);

                if (!silent) {
                    setMsg({ type: 'success', text: isDraft ? 'Draft saved!' : 'Project submitted successfully!' })
                    if (!isDraft) setTimeout(() => navigate('/developer-my-property'), 2000)
                }
                return true
            } else {
                if (!silent) setMsg({ type: 'error', text: responseData.message || 'Operation failed' })
                return false
            }
        } catch (err) {
            if (!silent) setMsg({ type: 'error', text: 'Server error occurred' })
            return false
        } finally {
            if (!silent) setSubmitting(false)
        }
    }

    const validateStep = (step: number) => {
        let errs: string[] = []
        let newErrors: Record<string, boolean> = {}

        if (step === 1) {
            if (!form.name?.trim()) { errs.push("Project Name"); newErrors.name = true; }
            if (!form.listingType?.trim()) { errs.push("Listing Type"); newErrors.listingType = true; }
            if (!form.propertyType || form.propertyType.length === 0) { errs.push("Property Type"); newErrors.propertyType = true; }
            if (!form.overviewPriceRange?.trim()) { errs.push("Starting Price"); newErrors.overviewPriceRange = true; }
        } else if (step === 2) {
            if (!form.city?.trim()) { errs.push("City"); newErrors.city = true; }
            if (!form.location?.trim()) { errs.push("Area / Locality"); newErrors.location = true; }
            if (!form.address?.trim()) { errs.push("Exact Address"); newErrors.address = true; }
        } else if (step === 3) {
            if (!form.developerId?.trim()) { errs.push("Developer selection"); newErrors.developerId = true; }
        } else if (step === 4) {
            if (floorPlans.length === 0) { errs.push("At least one Floor Plan"); }
        } else if (step === 5) {
            if (!form.metaTitle?.trim()) { errs.push("Meta Title"); newErrors.metaTitle = true; }
            if (!form.slug?.trim()) { errs.push("URL Slug"); newErrors.slug = true; }
        } else if (step === 6) {
            if (!featuredImage && !featuredPreview) { errs.push("Featured Image"); newErrors.featuredImage = true; }
        }

        setErrors(newErrors)
        if (errs.length > 0) {
            setMsg({ type: 'error', text: `Incomplete Fields: ${errs.join(", ")}` })
            window.scrollTo(0, 0)
            return false
        }
        setMsg(null)
        return true
    }

    const handleNextWithDraft = async () => {
        if (!validateStep(currentStep)) return
        const success = await handleSubmit(undefined, 'Draft', false)
        if (success) { nextStep(); window.scrollTo(0, 0); }
    }

    const packageFeatures = useMemo(() => {
        const features = {
            galleryPhotos: 10,
            youtubeVideos: 0,
            virtualTours: 0,
            streetView: false,
            projectOffers: 0,
            googleMap: true
        };
        if (userProfile?.assignedPackage?.categories) {
            userProfile.assignedPackage.categories.forEach((cat: any) => {
                if (cat.features) {
                    cat.features.forEach((feat: string) => {
                        const cleanFeat = feat.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
                        if (cleanFeat.includes('Gallery Photos:')) {
                            features.galleryPhotos = parseInt(cleanFeat.split(':')[1]) || 10;
                        } else if (cleanFeat.includes('YouTube Videos:')) {
                            features.youtubeVideos = parseInt(cleanFeat.split(':')[1]) || 0;
                        } else if (cleanFeat.includes('360° Virtual Tours:')) {
                            features.virtualTours = parseInt(cleanFeat.split(':')[1]) || 0;
                        } else if (cleanFeat.includes('Project Offers:')) {
                            const val = cleanFeat.split(':')[1].trim();
                            if (val.toLowerCase() === 'no') features.projectOffers = 0;
                            else if (val.toLowerCase() === 'unlimited') features.projectOffers = 999;
                            else features.projectOffers = parseInt(val) || 0;
                        } else if (cleanFeat.includes('Google Street View:')) {
                            features.streetView = cleanFeat.split(':')[1].trim().toLowerCase() === 'yes';
                        } else if (cleanFeat.includes('Google Map:')) {
                            features.googleMap = cleanFeat.split(':')[1].trim().toLowerCase() === 'yes';
                        }
                    });
                }
            });
        }
        return features;
    }, [userProfile]);

    if (loadingProfile) {
        return (
            <UserDashboardLayout>
                <div className="py-20 text-center">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-bold text-black uppercase tracking-widest text-xs">Loading Workspace...</p>
                </div>
            </UserDashboardLayout>
        );
    }

    // packageExpiry null/undefined = Unlimited package (no expiry check needed)
    const hasActivePackage = !!(userProfile?.assignedPackage) && 
                           (!userProfile?.packageExpiry || new Date(userProfile.packageExpiry) > new Date());

    const cleanTitle = (title: string) => {
        let cleaned = title.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
        const mapping: Record<string, string> = {
            'LISTINGS SYSTEM': 'CORE',
            'MEDIA SYSTEM': 'DISPLAY',
            'LOCATION + SEARCH': 'LOCALITY',
            'VISIBILITY SYSTEM': 'BOOST',
            'LEAD MGMT': 'ENGAGE',
            'ADVANCED FEATURES': 'PREMIUM'
        };
        for (const [key, value] of Object.entries(mapping)) {
            if (cleaned.toUpperCase().includes(key)) return value;
        }
        return cleaned.replace(/^\d+\.\s*/, '').replace(/\s*\(.*\)$/, '').trim().toUpperCase();
    };

    const cleanFeature = (text: string) => {
        return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
    };

    const limitReached = userProfile?.remainingProjects !== undefined && userProfile.remainingProjects <= 0;

    // Block if no active package, OR if adding a NEW project (!id) and limit is reached
    if (!hasActivePackage || (!id && limitReached)) {
        return (
            <UserDashboardLayout>
                <div id="pricing-node" className="mb-20 pb-20">
                    <div className="text-center mb-16 mt-10">
                        <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <i className="fa-solid fa-crown text-[#D4A017] text-3xl"></i>
                        </div>
                        <h2 className="text-3xl font-black text-black uppercase italic tracking-tight mb-3">
                            {limitReached ? 'Project Limit Reached' : 'Subscription Required'}
                        </h2>
                        <p className="text-black font-medium mb-6">
                            {limitReached 
                                ? 'You have exhausted your allowed project listings. Please upgrade your package to add more projects.' 
                                : 'Please choose a package to start listing your premium projects.'}
                        </p>
                        <div className="w-20 h-1.5 bg-[#D4A017] mx-auto rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {availablePackages.map((pkg) => (
                            <div 
                                key={pkg._id} 
                                className={`group relative flex flex-col bg-white rounded-2xl shadow-xl border border-[#D4A017]/10 hover:border-[#D4A017]/40 transition-all duration-500 hover:-translate-y-2 overflow-hidden ${pkg.popular ? 'scale-[1.02] z-10 border-[#D4A017]/40 shadow-2xl' : ''}`}
                            >
                                {pkg.popular && (
                                    <div className="absolute top-6 -right-12 bg-[#D4A017] text-white text-[8px] font-black uppercase tracking-widest px-14 py-2 rotate-45 shadow-lg z-10 animate__animated animate__fadeIn">
                                        Elite
                                    </div>
                                )}

                                <div className="p-8 border-b border-gray-100 relative overflow-hidden bg-[#0F2A5F] text-white">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg shadow-lg mb-6" style={{ backgroundColor: `${pkg.color}20`, color: pkg.color }}>
                                        <i className={`fa-solid ${pkg.icon || 'fa-building'}`}></i>
                                    </div>
                                    <h3 className="text-2xl font-black tracking-tight mb-1 text-[#D4A017] uppercase italic leading-none">{pkg.name}</h3>
                                    <p className="text-[11px] font-bold text-gray-400 mb-6 line-clamp-1 italic">{pkg.tagline}</p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-4xl font-black tracking-tighter text-white">₹{pkg.price}</span>
                                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">/ {pkg.period}</span>
                                    </div>
                                </div>

                                <div className="p-8 flex-grow bg-gray-50/30">
                                    {pkg.categories?.map((cat: any, ci: number) => (
                                        <div key={ci} className="mb-6 last:mb-0">
                                            <h4 className="text-[11px] font-black text-black uppercase tracking-widest mb-3 border-b border-gray-200 pb-1">{cleanTitle(cat.title)}</h4>
                                            <ul className="space-y-3">
                                                {cat.features.map((feat: string, fi: number) => {
                                                    const isAvailable = !feat.includes('❌');
                                                    const cleanText = cleanFeature(feat);
                                                    return (
                                                        <li key={fi} className={`flex items-start gap-2 ${isAvailable ? '' : 'opacity-30'}`}>
                                                            <i className={`fa-solid ${isAvailable ? 'fa-circle-check text-[#D4A017]' : 'fa-circle-xmark text-gray-400'} text-xs mt-1`}></i>
                                                            <span className="text-[13px] font-bold text-black leading-tight italic">{cleanText}</span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    ))}
                                </div>

                                <div className="px-6 py-4 bg-white border-t border-gray-100">
                                    {(Number(pkg.price) === 0) ? (
                                        <button 
                                            onClick={() => handleFreePurchase(pkg)}
                                            disabled={submittingPkgId === pkg._id}
                                            className={`w-full rounded-full text-xs font-black uppercase tracking-[0.2em] transition-all shadow-lg active:scale-95 py-3.5 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2`}
                                        >
                                            {submittingPkgId === pkg._id ? (
                                                <><i className="fa-solid fa-circle-notch fa-spin"></i> Activating...</>
                                            ) : (
                                                <><i className="fa-solid fa-bolt"></i> Get Started Free</>
                                            )}
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => openCheckout(pkg)}
                                            className={`w-full rounded-full text-xs font-black uppercase tracking-[0.2em] transition-all shadow-lg active:scale-95 py-3.5 ${pkg.popular ? 'bg-[#D4A017] text-white hover:bg-[#8b4513]' : 'bg-[#0F2A5F] text-white hover:bg-black'}`}
                                        >
                                            Purchase via UPI
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Checkout & QR Payment Modal */}
                <AnimatePresence>
                    {selectedPkg && (
                    <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
                        <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeCheckout}
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        />
                        <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-2xl bg-white rounded-xl p-6 shadow-2xl overflow-hidden"
                        >
                        <div className="flex justify-between items-center w-full mb-4">
                            <div>
                            <h2 className="text-2xl font-bold text-slate-900">Secure Payment</h2>
                            <p className="text-sm text-slate-500 mt-1">Pay via UPI to complete purchase</p>
                            </div>
                            <button
                            onClick={closeCheckout}
                            className="w-10 h-10 rounded-md bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-600 transition-all font-bold flex items-center justify-center"
                            >
                            <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <div className="flex flex-col md:flex-row gap-6">
                            {/* QR Code Section */}
                            <div className="md:w-1/2 bg-slate-50 border border-slate-200 rounded-xl p-5 flex flex-col items-center text-center">
                            <p className="text-sm font-bold text-slate-700 mb-2">Scan QR Code to Pay</p>
                            <div className="text-xs font-medium text-slate-500 mb-4 bg-white px-4 py-1.5 rounded-md border border-slate-200 shadow-sm select-all">
                                UPI ID: {adminUpiId}
                            </div>

                            <div className="w-36 h-36 bg-white p-2 border border-slate-200 rounded-xl shadow-sm mb-4 flex items-center justify-center">
                                <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(
                                    `upi://pay?pa=${adminUpiId}&pn=My%20Property%20Guru&am=${extractAmount(
                                    selectedPkg.price
                                    )}&cu=INR`
                                )}`}
                                alt="UPI QR Code"
                                className="w-full h-full object-contain"
                                />
                            </div>

                            <div className="flex items-center justify-center gap-2 mt-auto">
                                <span className="text-sm font-medium text-slate-500">Amount to pay:</span>
                                <span className="text-2xl font-black text-[#0F2A5F]">₹{extractAmount(selectedPkg.price)}</span>
                            </div>
                            </div>

                            {/* Form Section */}
                            <div className="md:w-1/2 flex flex-col justify-center">
                            <form onSubmit={handlePurchase} className="w-full space-y-4">
                                <div className="space-y-2">
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Transaction ID (UTR)</label>
                                <input
                                    type="text"
                                    required
                                    value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    placeholder="Enter 12-digit UTR Number"
                                    className="w-full px-4 py-3 rounded-md border border-slate-300 focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] outline-none bg-white text-slate-900 font-medium text-sm transition-all shadow-sm"
                                />
                                <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">After successful payment via the QR code or UPI ID, please enter the 12-digit transaction reference number (UTR) to authenticate your payment.</p>
                                </div>

                                <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeCheckout}
                                    className="flex-1 py-3.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-sm transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingPurchase}
                                    className="flex-1 py-3.5 rounded-md bg-[#0F2A5F] text-white hover:bg-black font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70"
                                >
                                    {submittingPurchase ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Submitting...</> : 'Submit Payment'}
                                </button>
                                </div>
                            </form>
                            </div>
                        </div>
                        </motion.div>
                    </div>
                    )}
                </AnimatePresence>
            </UserDashboardLayout>
        );
    }

    return (
        <UserDashboardLayout>
            <div className="mb-6 disable-animations">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-bold text-black mb-1">Add Project Portfolio</h3>
                        <p className="text-black/60 text-[10px] font-black uppercase tracking-[3px]">Professional 8-block wizard for premium listing excellence.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/developer-my-property" className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest text-black transition-all shadow-sm">
                            <i className="fa-solid fa-arrow-left"></i> My Projects
                        </Link>
                    </div>
                </div>
            </div>

            {/* Stepper Tabs Header */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 p-4">
                <div className="d-flex items-center gap-3 overflow-x-auto pb-3 tabs-scrollbar">
                    {DEV_STEPS.map((s, idx) => {
                        const isCurrent = currentStep === idx + 1;
                        return (
                            <button
                                type="button"
                                key={idx}
                                onClick={() => {
                                    setCurrentStep(idx + 1);
                                }}
                                className={`d-flex align-items-center gap-2 px-4 py-3 rounded-4 transition-all whitespace-nowrap border fw-black text-[10px] uppercase tracking-wider ${
                                    isCurrent 
                                        ? 'bg-[#D4A017] text-[#05144b] border-[#D4A017] shadow-sm' 
                                        : 'bg-light text-[#05144b] border-gray-200 hover:bg-gray-100 hover:text-[#05144b]'
                                }`}
                            >
                                <span className={`w-5 h-5 rounded-3 d-flex align-items-center justify-center text-[10px] fw-bold ${
                                    isCurrent ? 'bg-[#05144b] text-white' : 'bg-secondary/15 text-[#05144b]'
                                }`}>
                                    {idx + 1}
                                </span>
                                <span>{s}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Stepper Header */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden">
                <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center text-white font-bold shadow-lg shadow-black/20">{currentStep}</div>
                            <div className="flex-1 min-w-0">
                                <span className="block text-[10px] font-bold text-black/60 uppercase tracking-widest">Step {currentStep}</span>
                                <h4 className="text-sm font-bold text-black italic leading-tight">{DEV_STEPS[currentStep - 1]}</h4>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-base font-black text-black">{Math.round((currentStep / 9) * 100)}% Complete</span>
                        </div>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-black transition-all duration-700" style={{ width: `${(currentStep / 9) * 100}%` }}></div></div>
                </div>
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${msg.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-rose-50 border border-rose-200 text-rose-900'}`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} text-lg`}></i>
                    <span className="font-bold text-xs uppercase tracking-wider">{msg.text}</span>
                    <button onClick={() => setMsg(null)} className="ml-auto opacity-40"><i className="fa-solid fa-xmark"></i></button>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="bg-white border border-slate-100 rounded-3xl p-6 md:p-8 shadow-sm mb-8">
                    {currentStep === 1 && <Step1BasicInfo form={form} setForm={setForm} handleChange={handleChange} errors={errors} />}
                    {currentStep === 2 && <Step2Location form={form} setForm={setForm} handleChange={handleChange} cities={cities} filteredLocalities={filteredLocalities} errors={errors} packageFeatures={packageFeatures} />}
                    {currentStep === 3 && <Step3Developer form={form} setForm={setForm} handleChange={handleChange} developers={developers} errors={errors} disabledDeveloper={true} />}
                    {currentStep === 4 && <Step4FloorPlan activeStep4Tab={activeStep4Tab} setActiveStep4Tab={setActiveStep4Tab} floorPlans={floorPlans} setFloorPlans={setFloorPlans} getImageUrl={getImageUrl} errors={errors} form={form} />}
                    {currentStep === 5 && <StepSEOTimeline form={form} setForm={setForm} handleChange={handleChange} errors={errors} seoImageFile={seoImageFile} setSeoImageFile={setSeoImageFile} seoImagePreview={seoImagePreview} setSeoImagePreview={setSeoImagePreview} />}
                    {currentStep === 6 && <Step8Media 
                        featuredPreview={featuredPreview} 
                        setFeaturedImage={setFeaturedImage} 
                        setFeaturedPreview={setFeaturedPreview} 
                        tpPreview={tpPreview} 
                        setTpImage={setTpImage} 
                        setTpPreview={setTpPreview} 
                        galleryPreviews={galleryPreviews} 
                        galleryIcons={galleryIcons}
                        handleGalleryUpload={handleGalleryUpload} 
                        updateGalleryIcon={updateGalleryIcon}
                        removeGalleryImage={removeGalleryImage} 
                        removeGalleryCategory={removeGalleryCategory}
                        brochureFile={brochureFile} 
                        setBrochureFile={setBrochureFile} 
                        form={form} 
                        setForm={setForm} 
                        handleChange={handleChange} 
                        errors={errors} 
                        packageFeatures={packageFeatures}
                    />}
                    {currentStep === 7 && <Step8Grid
                        form={form}
                        setForm={setForm}
                        handleChange={handleChange}
                        errors={errors}
                        selectedAmenities={selectedAmenities}
                        setSelectedAmenities={setSelectedAmenities}
                        globalAmenities={globalAmenities}
                        specifications={specifications}
                        setSpecifications={setSpecifications}
                        legalDocuments={legalDocuments}
                        setLegalDocuments={setLegalDocuments}
                        globalLandmarkCategories={globalLandmarkCategories}
                    />}
                    {currentStep === 8 && <Step10Offers form={form} setForm={setForm} errors={errors} packageFeatures={packageFeatures} />}
                    {currentStep === 9 && <StepContactInfo form={form} setForm={setForm} handleChange={handleChange} errors={errors} />}
                </div>

                <div className="flex items-center justify-between gap-4 mb-20">
                    <button type="button" className="px-8 py-4 rounded-2xl bg-white border border-slate-200 text-black font-black text-xs flex items-center gap-3 transition-all shadow-sm" onClick={() => currentStep === 1 ? navigate('/developer-my-property') : prevStep()}>
                        <i className="fa-solid fa-arrow-left"></i> {currentStep === 1 ? 'BACK' : 'PREVIOUS'}
                    </button>
                    {currentStep < 9 ? (
                        <button type="button" disabled={submitting} className={`group px-8 py-4 rounded-2xl font-black text-sm transition-all flex items-center gap-3 ${submitting ? 'bg-slate-400' : 'bg-black text-white hover:scale-105 active:scale-95 shadow-xl shadow-black/10'}`} onClick={handleNextWithDraft}>
                            {submitting ? 'SAVING...' : `CONTINUE TO ${DEV_STEPS[currentStep]?.toUpperCase() || ''}`}
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    ) : (
                        <button type="submit" disabled={submitting} className="group px-10 py-4 bg-emerald-600 text-white rounded-2xl font-black text-sm transition-all flex items-center gap-3 hover:scale-105 active:scale-95 shadow-xl shadow-emerald-950/20 disabled:opacity-50">
                            {submitting ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-paper-plane"></i>}
                            {submitting ? 'SUBMITTING...' : 'PUBLISH PROJECT'}
                        </button>
                    )}
                </div>
            </form>

        </UserDashboardLayout>
    )
}
